import type { ApiErrorBody } from '@ukinfo/shared';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: { path: string; message: string }[];

  constructor(status: number, body: ApiErrorBody | null, fallback: string) {
    super(body?.error?.message ?? fallback);
    this.name = 'ApiError';
    this.status = status;
    this.code = body?.error?.code ?? 'UNKNOWN';
    this.details = body?.error?.details ?? [];
  }

  /** Feldbezogene Meldungen fuer die Anzeige direkt am Formularfeld. */
  fieldErrors(): Record<string, string> {
    const result: Record<string, string> = {};
    for (const detail of this.details) {
      if (detail.path) result[detail.path] = detail.message;
    }
    return result;
  }
}

/** Meldung, wenn eine schreibende Aktion ohne Netz versucht wird. */
export const OFFLINE_WRITE_MESSAGE = 'Ohne Netz nicht möglich – diese Aktion braucht eine Verbindung. Versuch es noch einmal, sobald du wieder online bist.';
export const OFFLINE_READ_MESSAGE = 'Keine Verbindung zum Server – gespeicherte Einheiten findest du unter „Offline gespeichert“.';

/** Der Server war nicht erreichbar (kein Netz), im Unterschied zu einer Antwort mit Fehler. */
export function isNetworkError(error: unknown): boolean {
  return error instanceof ApiError && error.status === 0;
}

/**
 * Erreichbarkeit des Servers, wie die Anfragen sie erleben: navigator.onLine ist unzuverlaessig (WLAN ohne
 * Internet, Funkloch am Platz). Scheitert eine Anfrage am Netz, gilt der Server als nicht erreichbar, bis
 * eine Anfrage wieder durchkommt oder der Browser "online" meldet.
 */
let unreachable = false;
const reachabilityListeners = new Set<() => void>();
function setUnreachable(value: boolean): void {
  if (unreachable === value) return;
  unreachable = value;
  reachabilityListeners.forEach((listener) => listener());
}
export const isServerUnreachable = (): boolean => unreachable;
export function subscribeReachability(listener: () => void): () => void {
  reachabilityListeners.add(listener);
  return () => { reachabilityListeners.delete(listener); };
}
/** Nur fuer Tests. */
export const resetReachability = (): void => setUnreachable(false);
if (typeof window !== 'undefined') window.addEventListener('online', () => setUnreachable(false));

function offlineError(message: string): ApiError {
  return new ApiError(0, { error: { code: 'OFFLINE', message } } as ApiErrorBody, message);
}

type Query = Record<string, string | number | boolean | undefined | null | string[]>;

export function buildQuery(query?: Query): string {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      for (const entry of value) params.append(key, entry);
    } else {
      params.set(key, String(value));
    }
  }
  const s = params.toString();
  return s ? `?${s}` : '';
}

async function request<T>(
  method: string,
  path: string,
  options: { body?: unknown; query?: Query; formData?: FormData } = {},
): Promise<T> {
  const init: RequestInit = {
    method,
    credentials: 'same-origin',
    headers: {},
  };

  if (options.formData) {
    init.body = options.formData;
  } else if (options.body !== undefined) {
    init.headers = { 'Content-Type': 'application/json' };
    init.body = JSON.stringify(options.body);
  }

  // Schreiben braucht Netz: gleich eine verständliche Meldung statt "Failed to fetch".
  if (method !== 'GET' && typeof navigator !== 'undefined' && navigator.onLine === false) throw offlineError(OFFLINE_WRITE_MESSAGE);

  let response: Response;
  try {
    response = await fetch(`${path}${buildQuery(options.query)}`, init);
  } catch (error) {
    // fetch wirft nur, wenn der Server nicht erreichbar war.
    if (error instanceof TypeError) {
      setUnreachable(true);
      throw offlineError(method === 'GET' ? OFFLINE_READ_MESSAGE : OFFLINE_WRITE_MESSAGE);
    }
    throw error;
  }
  setUnreachable(false);

  if (response.status === 204) return undefined as T;

  const contentType = response.headers.get('content-type') ?? '';
  const payload = contentType.includes('application/json') ? await response.json() : null;

  if (!response.ok) {
    throw new ApiError(response.status, payload as ApiErrorBody | null, response.statusText);
  }
  return payload as T;
}

export const api = {
  get: <T>(path: string, query?: Query) => request<T>('GET', path, { query }),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, { body }),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, { body }),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, { body }),
  del: <T>(path: string, body?: unknown) => request<T>('DELETE', path, { body }),
  upload: <T>(path: string, formData: FormData) => request<T>('POST', path, { formData }),
};
